import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "./LoadingSpinner";

interface PrivateRouteProps {
  children: React.ReactNode;
}

/**
 * Guards player/coach routes.
 * - Auth resolving → spinner (prevents flash-redirect before Firebase responds)
 * - Unauthenticated → /login, carrying the intended path as `state.from` so
 *   Login can return them to it after signing in
 * - Unverified email → /verify-email
 * - Staff (admin/support/root) → /admin  (staff have no player-facing pages)
 * - Root bypasses this guard (root can view everything)
 */
export default function PrivateRoute({ children }: PrivateRouteProps) {
  const { currentUser, userProfile, loading, emailVerified, isStaff, isRoot } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner size="lg" fullScreen />;
  if (!currentUser) return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  if (userProfile && !emailVerified) return <Navigate to="/verify-email" state={{ from: location.pathname + location.search }} replace />;
  // Staff users who are not root have no business on player-facing pages
  if (userProfile && isStaff && !isRoot) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}
